import React, { useEffect, useRef, useState } from 'react';
import { Row, Col, RadioChangeEvent, Button, Modal, Spin } from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useParams } from "react-router-dom";
import { getTest, startTest, sendResult } from '../../services/testApi';
import Result from './Result';
import TestPassage from './TestPassage';
import TestStartInfo from './TestStartInfo';
import { TestSession, updateTestSession, deleteTestSession, getTestSession } from '../../utils/testSession';
import type { TestAnswersMap, TestModel, TestQuestion, TestResult, TestStartResponse, TestType } from './types';
interface TestProps {
  testType?: TestType;
}

const Test: React.FC<TestProps> = ({ testType = 'default' }) => {
  const [test, setTest] = useState<TestModel | null>(null);
  const [questions, setQuestions] = useState<TestQuestion[]>([]);
  const [currentQuestionNum, setCurrentQuestionNum] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState<TestQuestion | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isStarted, setIsStarted] = useState<boolean>(false);
  const [answers, setAnswers] = useState<TestAnswersMap>({});
  const [resultId, setResultId] = useState(0);
  const [result, setResult] = useState<TestResult | null>(null);
  const [currentTimer, setCurrentTimer] = useState(0);
  const [timerStart, setTimerStart] = useState(0);
  
  const { id = '0' } = useParams<{ id: string }>();

  useEffect(() => {
    if (!isStarted) return;
    if (currentTimer > 0) {
      const t = setTimeout(() => {
        setCurrentTimer(Math.max(0, timerStart - Math.round(Date.now()/1000)));
      }, 1000);
      return () => clearTimeout(t);
    }
    if (isStarted && currentTimer <= 0) finishTest(resultId, answers);
  }, [answers, currentTimer, id, isStarted, resultId, timerStart]);

  useEffect(() => {
    const checkForActiveAttempt = async () => {
      const session = getTestSession({ type: testType, testId: Number(id) });
      setIsLoading(true);

      try {
        const loadedTest = await getTest(+id, testType);
        setTest(loadedTest);

        if (!session) return;

        await startAttempt({ isResume: true, session });
      } catch {
        if (session) {
          deleteTestSession();
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkForActiveAttempt();
  }, [id, testType]);

  const handleStart = async () => {
    deleteTestSession();
    setResult(null);
    setIsLoading(true);
    try {
      await startAttempt({ isResume: false });
    } catch (e: any) {
      setIsLoading(false);
      Modal.error({ title: 'Ошибка', content: String(e?.errorData?.error ?? e?.errorData?.message ?? 'Не удалось начать тест') });
      return;
    }
    setIsLoading(false);
  };
  const isStartingRef = useRef(false);
  const startAttempt = async ({ isResume, session }: { isResume: boolean; session?: TestSession }) => {
    if (isStartingRef.current) return;
    isStartingRef.current = true;
    const response: TestStartResponse = await startTest(+id, isResume, testType).finally(() => isStartingRef.current = false);
    const loadedQuestions = Array.isArray(response?.questions) ? response.questions : [];
    if (!loadedQuestions.length) throw new Error('Нет данных теста');

    const restoredQuestions = isResume ? orderQuestions(loadedQuestions, session?.questionOrder) : loadedQuestions;

    const restoredAnswers = isResume ? (session?.answers ?? {}) : {};
    const finalResultId = Number(response?.attemptId) || session?.attemptId;
    const finalExpiresAt = getExpirationTime(Number(response?.secondsLeft), session?.expiresAt);

    if (!finalResultId || !finalExpiresAt) {
      throw new Error('Некорректные данные попытки');
    };

    fillAttemptData({
      restoredQuestions,
      restoredAnswers,
      finalResultId,
      finalExpiresAt,
      startFromFirstQuestion: !isResume,
    });
  };

  const orderQuestions = (list: TestQuestion[], order: number[] = []) => {
    return [...list].sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  };

  const getExpirationTime = (secondsLeft: number, sessionExpiresAt?: number) => {
    const now = Math.round(Date.now() / 1000);
    return secondsLeft > 0 ? now + secondsLeft : sessionExpiresAt || now + Number(test?.testTime ?? 0);
  };

  const fillAttemptData = ({ restoredQuestions, restoredAnswers, finalResultId, finalExpiresAt, startFromFirstQuestion }: {
    restoredQuestions: TestQuestion[];
    restoredAnswers: Record<number, number[]>;
    finalResultId: number;
    finalExpiresAt: number;
    startFromFirstQuestion: boolean;
  }) => {
    const now = Math.round(Date.now() / 1000);
    const firstUnansweredIndex = restoredQuestions.findIndex((question) => !restoredAnswers[question.id]?.length);
    const nextQuestionIndex = startFromFirstQuestion ? 0 : (firstUnansweredIndex === -1 ? restoredQuestions.length - 1 : firstUnansweredIndex);

    setQuestions(restoredQuestions);
    setCurrentQuestionNum(nextQuestionIndex);
    setCurrentQuestion(restoredQuestions[nextQuestionIndex] || null);
    setAnswers(restoredAnswers);
    setResultId(finalResultId);
    setTimerStart(finalExpiresAt);
    setCurrentTimer(Math.max(0, finalExpiresAt - now));
    setIsStarted(true);
    
    updateTestSession({
      type: testType,
      testId: Number(id),
      attemptId: finalResultId,
      answers: restoredAnswers,
      questionOrder: restoredQuestions.map((question) => question.id),
      expiresAt: finalExpiresAt,
    });
  };

  const radioChange = (e: RadioChangeEvent) => {
    const updated = { ...answers, [questions[currentQuestionNum].id]: [e.target.value] };
    setAnswers(updated);
    if (resultId && timerStart) {
      updateTestSession({
        type: testType,
        testId: Number(id),
        attemptId: resultId,
        answers: updated,
        questionOrder: questions.map((question) => question.id),
        expiresAt: timerStart,
      });
    }
  };

  const selectChange = (e: CheckboxChangeEvent) => {
    const currentQuestionNumId = questions[currentQuestionNum].id;
    const edited = answers[currentQuestionNumId] ? [...answers[currentQuestionNumId]] : [];
    if (e.target.checked) {
      edited.push(e.target.value);
    } else {
      edited.splice(edited.indexOf(e.target.value), 1);
    }
    const updated = { ...answers, [currentQuestionNumId]: edited };
    setAnswers(updated);
    if (resultId && timerStart) {
      updateTestSession({
        type: testType,
        testId: Number(id),
        attemptId: resultId,
        answers: updated,
        questionOrder: questions.map((question) => question.id),
        expiresAt: timerStart,
      });
    }
  };
  
  const handleNext = () => {
    if (currentQuestionNum === questions.length - 1) {
      finishTest(resultId, answers)
      return;
    }
    if (answers[questions[currentQuestionNum].id]?.length) {
      setCurrentQuestion(questions[currentQuestionNum + 1])
      setCurrentQuestionNum(currentQuestionNum + 1);
    }
    else Modal.error({ title: 'Нельзя оставлять вопрос без ответа' });
  };

  const isFinalizingRef = useRef(false);
  const finishTest = (finalResultId: number, finalAnswers: Record<number, number[]>) => {
    if (isFinalizingRef.current) return;
    isFinalizingRef.current = true;
    setIsLoading(true);
    const finishRequest = sendResult(+id, finalResultId, finalAnswers, testType);
    finishRequest.then(result => {
      deleteTestSession();
      setIsStarted(false);
      setTest(prev => prev ? ({
        ...prev,
        attemptsCount: result.attemptsCount,
        nextAttempt: result.nextAttempt,
        isPassed: result.isPassed
      }) : null);
      setResult(result);
      setIsLoading(false);
    }).catch((e: any) => {
      setIsLoading(false);
      Modal.error({ title: 'Ошибка', content: String(e?.errorData?.error ?? e?.errorData?.message ?? 'Не удалось завершить тест') });
    });
  };

  return (
    <Spin spinning={isLoading}>
      {test ? (
        <>
          {isStarted && currentQuestion && (
            <TestPassage
              testType={testType}
              timer={currentTimer}
              title={test.name}
              currentQuestionNum={currentQuestionNum}
              questionsLength={questions.length}
              currentQuestion={currentQuestion}
              questionIds={questions.map((question) => question.id)}
              answers={answers}
              onNext={handleNext}
              onRadioChange={radioChange}
              onSelectChange={selectChange}
            />
          )}
          {!isStarted && !result && !isLoading && (
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <TestStartInfo test={test} onStart={handleStart} />
              </Col>
            </Row>
          )}
          {result && <Result testId={id} testName={test?.name || ''} result={result} answers={answers} questions={questions} testType={testType} />}
        </>
      ) : !isLoading ? (
        <div style={{ textAlign: 'center', marginTop: 100 }}>
          <h2 style={{ color: '#A40207' }}>Материал не существует или был удален</h2>
          <Button type="primary" onClick={() => window.history.back()}>Назад</Button>
        </div>
      ) : null}
    </Spin>
  );
};

export default Test;
